import { useMemo } from "react";

interface Props {
  /** Number of pieces. Keep ≤40 — each is one absolutely-positioned span. */
  count?: number;
  /** Piece colors; defaults to the portal palette + gold/white. */
  colors?: readonly string[];
}

const DEFAULT_COLORS = [
  "#ff4ed8",
  "#9a45ff",
  "#45d8ff",
  "#fbbf24",
  "#34d399",
  "#ffffff",
] as const;

/**
 * CSS-only confetti burst — pieces spawn above the container top edge and
 * fall through it once (forwards fill ends transparent, so leaving the
 * component mounted after the burst costs nothing). Parent must be
 * position:relative (or the overlay itself); we cover it with inset-0.
 *
 * Used for the "big win" moments only: reward reveal and session finish —
 * per game-juice guidance, celebration size should match achievement size.
 */
export function Confetti({ count = 28, colors = DEFAULT_COLORS }: Props) {
  // Randomised once per mount — every burst looks different, and re-mounting
  // (via key) replays it.
  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 350,
        duration: 1300 + Math.random() * 900,
        size: 6 + Math.random() * 6,
        color: colors[i % colors.length]!,
        drift: (Math.random() - 0.5) * 180,
        spin: 360 + Math.random() * 720,
      })),
    [count, colors]
  );

  return (
    <div
      className="pp-confetti absolute inset-0 overflow-hidden pointer-events-none select-none"
      aria-hidden
    >
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: -14,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.45,
            background: p.color,
            borderRadius: 2,
            opacity: 0,
            ["--drift" as never]: `${p.drift}px`,
            ["--spin" as never]: `${p.spin}deg`,
            animation: `pp-confetti-fall ${p.duration}ms ease-in ${p.delay}ms forwards`,
          }}
        />
      ))}
    </div>
  );
}
